import type { ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  PERFORMANCE_PAYMENT_SUBPRODUCT_KEY,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { offersBidResult } from "@/lib/bidResult";
import { useBondOrder } from "@/lib/useBondOrder";
import { useSubproductIdByKey } from "@/lib/usePreapprovalSubproduct";
import {
  BID_OUTCOMES,
  BidResultForm,
  type BidOutcome,
} from "@/components/BidResultForm";
import { PageHeader } from "@/components/PageHeader";
import { BondStatusChips } from "@/components/BondListCells";

// BRK-S183 — the bid result screen (`/surety/submissions/:id/bid-result`).
//
// The wave-1 `BidResultPanel` (BRK-S141) was a select inside the profile. The
// scope's flow is two screens: THIS one — a summary of the request and two
// buttons, Won and Lost — and the won-bid screen after it (BRK-S184). Lost is
// finished here; Won continues without saving anything. The summary and the
// question are `BidResultForm` (BRK-S422), which the submission page's Bid
// Result dialog renders too, so the two never disagree on a rule.
//
// ── THE REMINDER'S ONE-CLICK LINKS LAND HERE, AND SAVE NOTHING ───────────────
//
// PMS-S464's `bid_result_needed` mail links to
// `/surety/submissions/{id}?focus=bid-result[&result=won|lost]`; the profile
// redirects those here, carrying `?result=`. It PRESELECTS the outcome and
// nothing more: the link is an unauthenticated GET in a mail client, so the
// broker still confirms. A stale link never proposes a change over a result
// already on file — the record renders, and Update is how it changes.

export function BidResultPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const suggested = BID_OUTCOMES.find((o) => o === params.get("result"));
  const { order, setOrder, error } = useBondOrder(id);
  const ppSubproductId = useSubproductIdByKey(
    PERFORMANCE_PAYMENT_SUBPRODUCT_KEY,
  );

  if (error) {
    return (
      <Shell>
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
        <BackLink id={id} />
      </Shell>
    );
  }
  if (!order || ppSubproductId === undefined) {
    return (
      <Shell>
        <p role="status" className="text-sm text-brk-dim">
          Loading bid result…
        </p>
      </Shell>
    );
  }
  return (
    <BidResultScreen
      order={order}
      onUpdated={setOrder}
      offered={offersBidResult(order, ppSubproductId)}
      suggested={suggested}
    />
  );
}

function BidResultScreen({
  order,
  onUpdated,
  offered,
  suggested,
}: {
  order: BondSubmission;
  onUpdated: (order: BondSubmission) => void;
  offered: boolean;
  suggested: BidOutcome | undefined;
}) {
  return (
    <Shell
      header={
        <PageHeader
          title="Bid result"
          status={<BondStatusChips item={order} />}
          meta={
            <span className="break-words">
              <span className="tabular-nums">{order.number}</span>
              {order.accountLegalName ? ` · ${order.accountLegalName}` : ""}
            </span>
          }
        />
      }
    >
      <BidResultForm
        order={order}
        onUpdated={onUpdated}
        offered={offered}
        suggested={suggested}
      />
      <BackLink id={order.id} />
    </Shell>
  );
}

/** The page's one header (BRK-S262's PageHeader) over its body, on the
 *  shell's padding like every portal page. */
function Shell({
  header = <PageHeader title="Bid result" />,
  children,
}: {
  header?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      {header}
      <section className="flex min-w-0 flex-col gap-6">{children}</section>
    </>
  );
}

function BackLink({ id }: { id: string }) {
  return (
    <Link
      to={`/surety/submissions/${id}`}
      className="text-sm text-brk-accent hover:underline"
    >
      ← Back to the request
    </Link>
  );
}
